import { IAnalyticsData } from './../services/analytics/analytics_service_interface';
import { IJackpotDataById } from './../services/live_feeds/jackpots/jackpot_feeds_service_interface';
/* eslint-enable no-unused-vars */
import _Logger from '../utils/logger';
import Initialize from './initialize';
import { SYSTEM_EVENTS } from '../events/events';
import Event from '../utils/event';
import {SDK_VERSION} from '../sdk-version';
/*
 we can't use ServiceFactory instead of ConfigService
 because ConfigService is used in the initialize()
 */
import ConfigService from '../services/config/config_service';
import OptionsComponentController from '../components/options/options_component_controller';
import _ from '../utils/underscore';
import Device from '../utils/device/device';
import ServiceFactory from '../services/service_factory';
import Debug from './debug';
import Directive from './directive';
import {IAutoLoginParams} from '../../../common/interfaces/autologin-params.interface';
import { BalanceType } from '../../../common/const/wallet_balance_type';
import IStructuredLoggingService from "../../../common/interfaces/structured-logging-service-interface";
import {SdkOverlayZIndex} from "../../../common/interfaces/sdk-overlay-z-index";
import {PRIORITY_LEVEL, PRIORITY_LEVEL_ZINDEX_MAPPER} from "../services/overlay_manager/overlay_manager_levels";
import {ICashierWidgetsEligibilityDetails} from "../../../common/interfaces/cashier-widgets-eligibility-details.interface";
import {IGeoLocationInfo} from "../../../common/interfaces/geo-location-info.interface";
import {NullableNumber} from "../../../common/types/nullable-types";
import {IScriptLoaderService} from "../../../app-react/services/scrip-loader/script-loader.service.interface";
import {GEO_LOCATION} from "../services/geo_location/geo_consts";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {ISdkMessageButtonOptions} from '../../../common/interfaces/sdk-message-button-options';
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {IAbTestingFeatureProperties} from "../../../common/interfaces/ab-testing-interface";



const Logger = new _Logger('API');

/**
 * API class handle of the application initialization and it's the only thing
 * That bare to the client.
 *
 * Usage:
 *
 * When we create new instance of API class we initialize the CGP. (or your var name in the cgp.js).
 *
 * Blow are few examples of how we can use this API:
 *
 * Initialize the core and all the functionality of the application.
 * And the only thing we will bare to our client.
 *
 * options parameter is all the data you want to set in the initialize to your application.
 * this.Core.GetEvent() is an singleton event handler for event driven patten.
 *
 *    initialize(options = {}) {
 *           new this.Initialize(options);
 *           return this;
 *    }
 *
 * You can add or remove the set functions because they are chainable methods.
 * The method return a promise with a property of request that
 * contain a reference to the current xhr.
 */
export class API {
    debug = Debug || null;
    version = SDK_VERSION;

    serviceFactory: ServiceFactory = null;

    _isSdkReady: boolean;

    /**
     * @constructor
     * @param modelSettings
     */
    constructor(modelSettings) {
        this.serviceFactory = ServiceFactory.create();
        ConfigService.create().setDefaultConfig(modelSettings);
        this._isSdkReady = false;
        Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, () => {
            this._isSdkReady = true;
        }, this);
    }

    /**
     * Used by automation
     * @return {boolean}
     */
    isSdkReady() {
        return this._isSdkReady;
    }

    /**
     *
     * @returns {string}
     */
    generateGUID() {
        return Device.generateGuid();
    }

    /**
     * @returns {API}
     * @public
     */
    initialize(userSettings, onInitCompleteCallback) {
        ConfigService.create().setUserConfig(userSettings);
        new Initialize(onInitCompleteCallback);
        return this;
    }

    getConfigs() {
        return ConfigService.create().getConfig();
    }

    /**
     * Logout from the application will remove access token and refresh token from local storage.
     * User will not be allowed anymore to preform an authentication role request to resource server until he authorize again.
     *
     * @usage:
     *      CGP.logout(); // Will remove access token from local storage and the user will not allowed to preform an authentication role request to resource server.
     *
     *
     * @public
     */
    logout() {
        return this.serviceFactory.oAuth2Service().logout();
    }

    runAutoLogin(autoLoginParams: IAutoLoginParams): Promise<void> {
        let sessionTimer: TimeSpan | undefined = undefined;
        if(Check.isNumber(autoLoginParams.sessionTimer)) {
            sessionTimer = TimeSpan.fromSeconds(autoLoginParams.sessionTimer);
        }
        return this.serviceFactory.oAuth2Service().runAutoLogin(autoLoginParams.token, sessionTimer, autoLoginParams.sourceProductPackage, autoLoginParams.brandId, autoLoginParams.sourceSubBrand, autoLoginParams.targetSubBrand);
    }


    /**
     * Open login form over overlay externally.
     * @example:
     *
     * // The open login form take these default values
     * var loginViewOptions = {
     *      marketingDataFn : 'getMarketingData(1234,abc,312312,null)'
     * }
     *
     * function onClose() {
     *    console.log('When clicking on the close overlay btn I will invoke!');
     * }
     *
     * function onSuccess() {
     *    console.log('When the login authentication will be successful I will invoke!');
     * }
     *
     * CGP.openLoginForm(loginViewOptions, onClose.bind(this), onSuccess.bind(this));
     *
     *
     *  @returns void
     */
    openLoginForm(marketingParams?: MarketingParams) {

        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.resolve();
        }

        return this.serviceFactory.webLoginService().openLoginForm({
            marketingParams: marketingParams,
            onCloseCallback: () => {
                Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, { category: 'Client_LoginPopup', action: 'Close' });
            }
        });
    }

    /**
     * Message popup CGP extra candy.
     *
     * @param {string} title - title of the message # Require
     * @param {string} text - text in the message
     * @param {MessageType} type - the type of the message
     * @param {MessagePriority} priority - the priority of the message
     * @param {MessageButton[]} buttons - array of string that represent button action
     */
    message({ title, text, type, priority, buttons }) {
        //   MessageManager.create().addMessage(new MessageModel({title, text, type, priority, buttons}));
    }

    /**
     * Get a json object menu.
     *
     * @returns {*|Array}
     */
    menuJSON() {
        return this.serviceFactory.menuService().getJSON();
    }


    /**
     * Get a markup html menu.
     *
     * @returns {string|Element}
     */
    menuHTML() {
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, { category: 'Client_UserMenu', action: 'AppSettings' });
        return this.serviceFactory.menuService().getHTML();
    }


    /**
     *
     * You also may to get reference to the overlay manager and fill it and create the display after the
     * Model is full.
     *
     * @usage:
     *  Full demonstration of the overlay options.
     *
     * @example 1:
     *      var el = document.createElement('DIV'); // Create arbitrarily DOM Element object.
     *      var over = CGP.openOverlay(el); // Call overlay function from cgp api and append the el in the function.
     *
     *  Close ability you the call.
     *      closeLastOverlay(); // will close the overlay and remove it from the DOM.
     *
     *
     * @param overlayOptions - Object {
     * el (object) - DOMElement Object that will be over the overlay,
     * priority (number) - The overlay priority level
     * id (number) - overlay id. If is not provided, the iverlay will be opened with incremented id from previous overlay. Will be deprecated later
     * isCloseBtnAllowed (bool) - determinate if overlay will be opened with close button. Default is true
     * }
     * @public
     */
    openOverlay(overlayOptions) {
        Event.fire(SYSTEM_EVENTS.OPEN_OVERLAY, overlayOptions);
    }


    /**
     * Close last overaly dialog appropriate to its priority level
     * @param priority
     * priority (number)  The overlay priority level
     */
    closeLastOverlay(priority) {
        Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, { priority: priority });
    }

    getRegulationService() {
        return this.serviceFactory.regulationService();
    }

    /**
     * Close all overlay in the application.
     */
    closeAllOverlay() {
        Event.fire(SYSTEM_EVENTS.CLOSE_ALL_OVERLAY);
    }

    /**
     * Give access to device component.
     */
    device() {
        return Device;
    }


    isGameRunning(){
        return this.serviceFactory.gameService().isGameRunning();
    }


    getRegulationDateAndTimeFormat() {
        const skinService = this.serviceFactory.skinService();

        return {
            date: skinService.getDateFormat(),
            time: skinService.getTimeFormat(),
            dateTime: skinService.getDateTimeFormat(),
            dateTimeHistory: skinService.getDateTimeHistoryFormat()
        };
    }

    /**
     * Return list with id's of recently played games
     */
    getRecentlyGames() {
        return this.serviceFactory.recentlyPlayedService().getRecentlyPlayedList();
    }

    /**
     * Event Emitter give you the power to subscribe CGP system event handling.
     *
     * @usage:
     * // Simple use of CGP Event Subscriber we allowed to give the programmer the best way to integrate with CGP SDK JS.
     *
     * @example 1:
     * var oauthChangeEvent = CGP.onEvent({eventName: 'OAUTH_STATE_CHANGE'}); // now you will get notify when wallet change.
     *
     * @example 2:
     * // You can modify and add callback when the event been fired.
     * var oauthChangeEvent = CGP.onEvent({eventName: 'OAUTH_STATE_CHANGE', callback: function(){console.log('user wallet been changed')}})
     *
     * @example 3:
     * // We also giving the developer the relief of binding this to the callback context.. for Ecmascript 5 noobs
     * var oauthChangeEvent = CGP.onEvent({eventName: 'OAUTH_STATE_CHANGE', callback: function(){console.log('user wallet been changed')}, context: this)
     *
     * // Every variable subscribe come with off function that can be unsubscribe the event from listening.
     * oauthChangeEvent.off();
     *
     * // When you trying to subscribe to event that not exist or not allowed to be subscribe you will get undefined.
     * var oauthChangeEvent = CGP.onEvent('SOME_CUSTOM_EVENT_NOT_ALLOWED', function(){console.log('user wallet been changed')}, this)
     *
     * // The events we allowed to used are restricted due to CGP product design. for more information you can see full documentation in
     * // http://nocipedia/index.php?w=WEB&title=CGP:onEvent
     *
     *
     * @param eventOptions - Object {
     * eventName - Is the event name to subscribe to,
     * callback - Is the function that execute when the event fired. function() {alert('event fired')},
     * context - Is the context of the execution event callback.
     * }
     * @returns {*}
     */
    onEvent(eventOptions) {
        return Event.on(eventOptions.eventName, eventOptions.callback, eventOptions.context);
    }

    /**
     * Getter for the Username
     * if not authed, will return a false.
     *
     * @usage
     * var username = CGP.getUsername();
     *
     * @returns {String|Boolean}
     */
    getUsername() {
        if (this.isAuthorized()) {
            return this.serviceFactory.userService().getUsername();
        }

        return false;
    }

    getSessionId(){
      let oAuth2Service = this.serviceFactory.oAuth2Service();
      if (oAuth2Service.isAuthorized()) {
        return oAuth2Service.getToken().sessionId;
      } else {
        return null;
      }
    }

    /**
     * Is authorized giving the developer the power to know if the current user is authorized to the system or not.
     *
     * @usage
     * var isConnected = CGP.isAuthorized();
     * If the user is authorized to the system the output will be true else false.
     *
     * @example 1:
     * if (CGP.isAuthorized()) {
     *   console.log('Hi I'm loggedin');
     * }
     *
     * @returns {*|boolean}
     */
    isAuthorized() {
        return this.serviceFactory.oAuth2Service().isAuthorized();
    }

    isLoginFromRegistration(){
        return this.serviceFactory.oAuth2Service().isLoginFromRegistration();
    }


    isVIP() {
        return this.serviceFactory.oAuth2Service().isVIP();
    }


    getUserData() {
        return this.serviceFactory.oAuth2Service().getUserData();
    }


    getUserSegments() {
        return this.serviceFactory.integrationService().getUserSegments();
    }


    getSerial() {
        let marketingData: any;
        let GNWPRequestModel: any;
        return this.serviceFactory.skinService().getSerialData(marketingData, GNWPRequestModel);
    }

    getHybrid() {
        return this.serviceFactory.hybridService();
    }

    isHybridEmbeddedGame(gameType: number): boolean {
        return this.serviceFactory.hybridService().isOnDemandHtmlGame(gameType);
    }

    openHybridLink(url: string) {
        return this.serviceFactory.hybridService().openExternalLink(url);
    }

    getUtils() {
        return _;
    }

    displayHybrid() {
        this.getHybrid().displayHybrid();
    }


    getProductPackage() {
        return this.serviceFactory.skinService().getProductPackageID();
    }

    getSkin() {
        return this.serviceFactory.skinService();
    }


    /**
     * Open External link as iframe or in new window.
     * External link may be a key for predefined navigation or any other link for external web resource
     * Predefined navigation takes care to open specific links in default target (iframe or window) as it defined in documentation
     * For example, cashier should not be opened in new tab, so its default is iframe, otherwise, my account is defined to be opened in window
     *
     * @usage
     *
     * @example1
     * Open My account in default target (new window)
     * var linkParams = {source: 'my_account_mobile' }
     * CGP.openExternalLink(linkParams)
     *
     * @example2
     * Open My account in iframe
     * var linkParams = {source: 'my_account', view: 'iframe', options: {} }
     * CGP.openExternalLink(linkParams)
     *
     * @example3
     * Open cashier in Deposit page with specified promo code
     * var linkParams = {source: 'cashier', options: {sessionType: 1, promCode: 12345} }
     * CGP.openExternalLink(linkParams)
     *
     * @example4
     * Open regular web link. Note, by default all external links are opened in iframe
     * All parameters are passed in 'options' parameter will be parsed to query string, so it's no support for complex objects like {param: {....}}
     * var linkParams = {source: 'www.external-link.com', options: {param1: 'abc', param2: 'def'....}}
     * CGP.openExternalLink(linkParams)
     * The result link is www.external-link.com?param1=abc&param2=def
     *
     * @params {object} linkParams {
     * source {string}  - link or predefined key to open
     * view {string} - target that link will be open in. Values: 'iframe', 'window'. Default for regular link is 'iframe', for predefined - see documentation
     * options {object}  - addinional parameters for link. Used for open page in specified mode
     * marketing - the b2c or whatever provider who will use us can send marketing data.
     * }
     *
     */
    openExternalLink(linkParams) {
        if (linkParams.marketing) {
            Event.fire(SYSTEM_EVENTS.INCOMING_MARKETING_DATA, linkParams.marketing);
        }

        return this.serviceFactory.openLinkService().openLink(linkParams);
    }

  openResponsibleGamingLink(view?:number){
    this.serviceFactory.openLinkService().openResponsibleGamblingLink(view);
  }

    /**
     * open tab in new window - if Hybrid call call external link function
     */
    openInNewWindow(url, specs) {
        this.serviceFactory.windowService().openInNewWindow(url, specs);
    }

    /**
     * Get user token if exist.
     *
     * @usage
     * var token = CGP.getToken(); // if the user is authorized token will received it.
     *
     * @returns {Promise}
     */
    getToken() {
        return this.serviceFactory.oAuth2Service().getToken();
    }

    getPlatformToken(){
        return this.serviceFactory.userService().getPlatformToken();
    }

    getFirebaseToken(){
        return this.serviceFactory.userService().getFirebaseLoginToken();
    }

    getTrendingRecommendedGamesForPlayer(listTypeIds: number[]):Promise<{[key:number] : number[]}> {
        return this.serviceFactory.aiListService().getTrendingRecommendedGamesForPlayer(listTypeIds);
    }

    /**
     * Generates a platform token for autologin.
     *
     * @returns
     */
    getAutoLoginToken() {
        return this.serviceFactory.userService().renewLoginToken();
    }


    /**
     * Login a user using an AutoLogin/UserInfo token - provided usually by Platform.
     *
     * @returns
     */
    setAutoLoginToken(token) {
        Event.fire(SYSTEM_EVENTS.RUN_AUTOLOGIN, token);

        return this;
    }


    /**
     * Open Options
     * ReminderSettings, About,TouchId
     */
    openOptions(screenType, innerScreen) {
        const screen = OptionsComponentController.create().load(screenType);
        if (undefined !== innerScreen) {
            screen.setScreen(innerScreen);
        }

        screen.render();

        if (undefined !== innerScreen) {
            if (undefined !== this.getHybrid().getTouchIDFeaturer()) {
                this.getHybrid().getTouchIDFeaturer().setTouchIdComponentController(screen);
            }
        }
    }

    /**
     * get sub brand ID
     * @returns {*}
     */
    getSubBrandID() {
        return this.serviceFactory.skinService().getSubBrandID();
    }

    /**
     * get external brand callback url
     */
    getExternalBrandCallbackURL() {
        return this.serviceFactory.queryStringDetectorService().getExternalBrandCallbackURL();
    }

    /**
     * get external brand callback method
     */
    getExternalBrandCallbackURLMethod() {
        return this.serviceFactory.queryStringDetectorService().getExternalBrandCallbackURLMethod();
    }

    /**
     * get source sub brand name.
     */
    getSourceSubBrandName() {
        const sourceSubBrandID = this.serviceFactory.queryStringDetectorService().getSourceSubBrand();
        let sourceSubBrandName = "notFound";

        // We need it for back to. and also to recognize poker native.
        const pokerSubBrands = ['1', '114', '81', '45', '130', '106', '103'];
        const sportSubBrands = ['8', '115', '110', '116', '131', '128'];

        if (pokerSubBrands.includes(sourceSubBrandID)) {
            sourceSubBrandName = "poker";
        } else if (sportSubBrands.includes(sourceSubBrandID)) {
            sourceSubBrandName = "sport";
        }

        return sourceSubBrandName;
    }

    /**
     * get brand ID
     * @returns {*}
     */
    getBrandID() {
        return this.serviceFactory.skinService().getBrandID();
    }

    showInfo(txt) {

        let options = {
            hasImage: false,
            text: txt,
            closeButton: true,
            title: 'Info',
            buttons: [
                { name: 'Close', isClosable: true },
                {
                    name: 'MailTo:', isClosable: true, callback: () => {
                        this.getHybrid().openMail(encodeURIComponent(txt));
                    }
                }
            ]
        };
        this.openMessage(options);
    }

    openSafariTest(url) {
        this.getHybrid().openSafariTest(url);
    }

    navigateToSecondGameVebView(url) {
        Event.fire(SYSTEM_EVENTS.WEB_VIEW_NAVIGATE_GAME, url);
    }


    /**
     * Popup CGP messages in lightbox (overlay)
     *
     * @params
     * messageOptions {object} {
     * title {string}           message title text. It appears on the top of message dialog, but under icon if exists (read type and customIcon)
     * text {string}            message text. Can be every HTML markup. It appears under the title
     * type {string}            message type. There are strongly defined message types (see MESSAGE_TYPE const) [info, warning, error, success].
     *                          If one of them is passed and there is no customIcon, appropriate message icon will appear under the title.
     * customIcon {string}      url to custom icon. It overrides any message defined icon, that is to say, if you define message type, it will be ignored
     * priority {number}        message priority. Since the message opens on overlay, it defines the overlay priority. There are two priority types in CGP (HIGH and LOW).
     *                          default value for messages is PRIORITY_LEVEL.MESSAGE, so when your message will appear it will destroy all previous overlays and messages. If need to change this behaviour,
     *                          you should pass lower priority: PRIORITY_LEVEL.BASE, then your message will appear when the higher overlay destroys.
     *                          NOTE! If you pass priority and there is custom button in message (read button) that should close the message on click, be aware, you should pass priority
     *                          parameter in button onClick callback where you fire CLOSE_OVERLAY event
     *                          @Example
     *                          CGP.openMessage({priority: priorityValue}); //priorityValue =  PRIORITY_LEVEL.MESSAGE or PRIORITY_LEVEL.BASE (similar to what passed in message options)
     * closeButton {bool}       define if close X button will appear to close the message
     * buttons {array}          contains buttons on message dialog. Read button object definition in message_button.
     *                          By default, if the array is empty, message dialog render single default button with name 'OK' that close the dialog.
     *                          Each custom button by default closes message dialog. To prevent this behavior, isClosable button parameter will be define false explicitly. (see message_button).
     *                          @Example
     *                          [{name: 'btn1'}, {name: 'btn2', isClosable: false}]
     * buttonPosition {string}  define buttons position. Values: 'left', 'right', 'center'.
     *                          By default, in PC layout, button aligned to center, and in mobile to right side.
     *                          If buttonPosition is defined, it overrides this behaviour and valid both in PC and mobile
     *}
     */
    openMessage(messageOptions) {
        Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, messageOptions);
    }


    /**
     * Open history link.
     *
     * @returns {*|string|*}
     */
    openHistoryLink() {
        this.serviceFactory.integrationService().redirectToHistory();
    }

    /**
     * Open game session history link.
     *
     * @returns {*|string|*}
     */
    openGameSessionHistoryLink() {
        this.serviceFactory.integrationService().redirectToGameSessionHistory();
    }

    /**
     * Expose internal cgp events should be during development only!
     *
     * @returns {Event}
     */
    exposeEventInternal() {
        return Event;
    }

    structuredLoggingService():IStructuredLoggingService{
        return this.serviceFactory.structuredLoggingService();
    }

    getCGPMaxOverlayZIndex(): number {
        return this.serviceFactory.overlayManagerService().getCGPMaxOverlayZIndex();
    }

    /**
     * Send GA analytic object
     *
     * analytic {object} contains GA properties (category, action, label, value)
     *
     *
     * @param analyticObject
     */
    sendAnalytics(analyticObject: IAnalyticsData) {
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticObject);
    }

    pushDataToDataLayer(data){
        this.serviceFactory.analyticsService().pushDataToDataLayer(data);
    }

    getGoogleClientID() {
        return this.serviceFactory.analyticsService().getGoogleClientID();
    }

    /**
     * Add extra properties to GTA data layer
     * @param dataLayerObject
     */
    updateAnalyticsDataLayer(dataLayerObject) {
        Event.fire(SYSTEM_EVENTS.ANALYTIC_DATA_LAYER_UPDATE, dataLayerObject);
    }

    /**
     * Send specified event for trigger optimize experiment condition compare process
     */
    onSegmentationChanged() {
        Event.fire(SYSTEM_EVENTS.ANALYTICS_OPTIMIZE_TRIGGER);
    }


    /**
     * Execute specified menu action.
     *
     * @param menuId - menu id that its logic will be executed
     * @returns {*}
     */
    menuActionExecute(menuId) {
        return this.serviceFactory.menuService().executeAction(menuId);
    }


    /**
     * This function api gets a number type only in ` CENTS ` !.
     * Formatting cents due to user preference money format.
     * @param cents
     * @param roundIfZero if true and the decimal numbers are 0 value will return without the decimal number
     * @param options
     * @example #1: let jackpot = CGP.format$$$(1000000);
     *              console.log(jackpot) // $10,000.00
     * @example #2: let jackpot = CGP.format$$$(1000000, true);
     *              console.log(jackpot) // $10,000
     *
     * @example #3: let jackpot = CGP.format$$$(1000055, true);
     *              console.log(jackpot) // $10,000.55
     *
     * @returns {string}
     */
    format$$$(cents, roundIfZero = false) {
        if (!_.isNumber(cents)) {
            Logger.error('format$$$ invalid input => ', cents);
            return '';
        }

        return this.serviceFactory.currencyService().formatMoney(cents, {}, roundIfZero);
    }

    getCurrencyIso3(): string {
        return this.serviceFactory.currencyService().getCurrency();
    }


    kickSimulate(params) {
        this.serviceFactory.playerMessagesService().getMessageStrategy().KICKUpdateHandler(params.featureName, params.msgFields, params.fullTopic);
    }



    /**
     * Retrieves the games information like title from the backend
     * @param gameTypes - an array of game types
     * @param url - for using a URL on the fly (and not the service URI param)
     * @returns {Promise<T>} where T is a map where the key is the game type
     * Sample returned data:
     {
        130017: {gameType: 130017, title: 'Elm Street', background: 'someUrl'}
        130043: {gameType: 130043, title: 'Millionaire Genie', background: 'someUrl'}
     }
     */
    getGamesInfo(gameTypes) {
        return this.serviceFactory.gamesInformationService().getGamesInfo(gameTypes);
    }

    getGamesThemes(): Record<number, string> {
        return this.serviceFactory.gamesInformationService().themes;
    }

    getGamesVendors(): Record<number, string> {
        return this.serviceFactory.gamesInformationService().vendors;
    }

    getGamesCompanies(): Record<number, string> {
        return this.serviceFactory.gamesInformationService().companies;
    }

    /**
     * use this method only for history. this method will return also game info for restricted and not in layout games.
     * @param gameTypes
     */
    getGamesInfoForHistory(gameTypes){
        return this.serviceFactory.gamesInformationService().getGamesInfoWithoutAnyFiltering(gameTypes);
    }

    /**
     * Get all game IDs as array
     * @param url - for using a URL on the fly (and not the service URI param)
     * @returns {*}
     */
    getAllGameTypes() {
        return this.serviceFactory.gamesInformationService().getAllGameTypes();
    }

    freePlayAdd(topic, fields) {
        return this.serviceFactory.playerMessagesService().getMessageStrategy().FPUpdateHandler(null, fields, topic);
    }


    applyRestrictions(restrictions) {
        this.serviceFactory.restrictionsService().applyRestrictions(restrictions);
    }


    /**
     * Retrieves the AI (Artificial Intelligence) game list from the backend
     * @returns {Promise<T>} 2 Arrays one with game types and the other with the AI game score for each type
     * @param maxGames - max games for return
     * @param filterOutRecentlyPlayedGames - if true we will filter out from the response the recently played games
     * @param numberOfRecentlyPlayedGameToFilterOut - numberOfRecentlyPlayedGameToFilterOut number of recently games that we need to filter out
     * Sample returned
     * AiGameListData = {
     * "aiGameTypesArray": ["130001", "130008", "130012", "130014", "130017", "130024", "130026", "130036"],
     * "aiEngine":  the engine/vendor name that return the AI list = for example 888
     * };
     */
    getAIList(maxGames, filterOutRecentlyPlayedGames, numberOfRecentlyPlayedGameToFilterOut = 0) {
        return this.serviceFactory.aiListService().getAIList(maxGames, filterOutRecentlyPlayedGames, numberOfRecentlyPlayedGameToFilterOut);
    }

    getAINewGamesList(maxGames, filterOutRecentlyPlayedGames, numberOfRecentlyPlayedGameToFilterOut = 0) {
        return this.serviceFactory.aiListService().getAINewGamesList(maxGames, filterOutRecentlyPlayedGames, numberOfRecentlyPlayedGameToFilterOut);
    }

    // will become obsolete, do not use anymore
    getAbTestingDataForFeature(featureName) {
        return this.serviceFactory.abTestingService().getAbTestingDataForFeature(featureName);
    }

    getAbTestingData() {
        return this.serviceFactory.abTestingService().getAbTestingData();
    }

    /**
     * @returns Promise<Boolean>
     */
    waitForAnonymousAbTestingData() {
        return this.serviceFactory.abTestingService().waitForAnonymousAbTestingData();
    }

    getAnonymousPlayerAbTestingDataForFeature(featureName: string, shouldDefaultToLocalStorageData: boolean): IAbTestingFeatureProperties {
        return this.serviceFactory.abTestingService().getAnonymousPlayerAbTestingDataForFeature(featureName, shouldDefaultToLocalStorageData);
    }

    /**
     * @returns Promise<Number>
     */
    countPlayerChallenges() {
        return this.serviceFactory.userService().countPlayerChallenges();
    }

    routeChanged(routeParams) {
        Event.fire(SYSTEM_EVENTS.ROUTE_CHANGED, routeParams);
    }

    getGamesDiagnosticMessages() {
        return this.serviceFactory.gamesInformationService().getDiagnosticMessages();
    }

    getLoginTime() {
        return this.serviceFactory.oAuth2Service().getLoginTime();
    }

    OMGSimulate(messages) {
        if (!messages) {
            return;
        }

        messages.forEach((message) => {
            this.serviceFactory.playerMessagesService().getMessageStrategy().OMGUpdateHandler('OMG', message);
        });
    }

    getDepositWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
        return this.serviceFactory.userService().getDepositWidgetEligibilityDetails();
    }

    getCashoutWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
        return this.serviceFactory.userService().getCashoutWidgetEligibilityDetails();
    }

    getGRSEnvironments() {
        return this.serviceFactory.gameService().getGRSEnvironments();
    }

    getCorrelatedGames(gameType, numberOfGames = 3) {
        return this.serviceFactory.gamesCorrelationService().getCorrelatedGames(gameType, numberOfGames);
    }

    simulateClockRequest(data) {
        this.serviceFactory.clockService().setServerTime("2019-01-23T15:27:03Z");
        this.serviceFactory.clockService().setRegulationOffset(-300);
    }

    getCurrentServerDate() {
        return this.serviceFactory.clockService().getCurrentServerDate();
    }

    simulatePlayerMessages() {
        this.serviceFactory.playerMessagesService().getMessageStrategy()._getPlayerMessages();
    }


    createComponentRenderer(componentId) {
        return Directive.createComponentRenderer(componentId);
    }

    startLiveTableFeeds() {
        return this.serviceFactory.liveTablesService().start();
    }

    /**
     * show toaster according to param.
     * examples:
     * action toaster
     * CGP.showToaster({
            MessageID: 'message ' + Date.now(),
            Type: 'action_toaster',
            Title: 'PLEASE NOTE',
            SubTitle: 'In order to place bets you are required to upload your documents.',
            DurationInSec: 130,
            buttonText: "login",
            callback: ()=> {
                CGP.openLoginForm(null);
            },
            ShowDefaultImage: true
        })

     timed toaster
     * CGP.showToaster({
            MessageID: 'message ' + Date.now(),
            Type: 'timed_toaster',
            Title: 'HEADS UP',
            SubTitle: 'The Daily Jackpot is guaranteed to fall within the next 30 minutes!',
            DurationInSec: 30,
            ShowDefaultImage: true
        })

     ack toaster
     * CGP.showToaster({
            MessageID: 'message ' + Date.now(),
            Type: 'acknowledge_toaster',
            Title: 'CONGRATULATIONS!',
            SubTitle: 'You have been successfully upgraded to VIP.',
            ShowDefaultImage: true
        })

     override image
     CGP.showToaster({
            MessageID: 'message ' + Date.now(),
            Type: 'acknowledge_toaster',
            Title: 'CONGRATULATIONS!',
            SubTitle: 'You have been successfully upgraded to VIP.',
            close: false,
            PCImage: 'data:image/jpeg;base64,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'
        })
     * @param toasterData
     */
    showToaster(toasterData) {
        return this.serviceFactory.toasterService().showToaster(toasterData);
    }


    getTablesInfo() {
        return this.serviceFactory.liveTablesService().getTablesInfo();
    }

    stopLiveTableFeeds() {
        return this.serviceFactory.liveTablesService().stop();
    }

    testGamesQuestionnaire() {
        this.serviceFactory.gamesQuestionnaireService()._showOverlay();
    }

    getDailyDealCampaigns() {
        return this.serviceFactory.historyService().getDailyDealCampaigns();
    }

    getJackpotsWithState(): Record<number, IJackpotDataById> {
        return this.serviceFactory.jackpotFeedsService().getJackpotsWithState();
    }

    getJackpotAmountByGameType(gameType: number): NullableNumber {
        return this.serviceFactory.jackpotFeedsService().getJackpotAmountInCentsByGame(gameType);
    }

    getBankroll(balanceType: BalanceType, plainNumber: boolean = false) {
        return this.serviceFactory.walletService().getBankroll(balanceType, plainNumber);
    }

    loadSafezoneSdk(){
        return this.serviceFactory.safezoneLoaderService().loadSafezoneSdk();
    }

    getEnvironment(){
        return this.serviceFactory.structuredLoggingService().getEnvironment();
    }

    getCountryName() {
        return this.serviceFactory.countryService().countryName;
    }

    getCountryIso3() {
        return this.serviceFactory.countryService().getCountry();
    }

    getSupportedStates() {
        return this.serviceFactory.countryService().supportedStates;
    }

    getWrongLocationDetectionPath() {
       return this.serviceFactory.skinService().getWrongLocationDetectionPath();
    }

    clearLocalStorage() {
        localStorage.clear();
        Event.fire(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED);
    }

    private _onOrbitInitCompleteFired = false;
    onOrbitInitComplete() {
        if(!this._onOrbitInitCompleteFired) {
            this._onOrbitInitCompleteFired = true; // just in case somebody calls this function twice.
            Event.fire(SYSTEM_EVENTS.ON_ORBIT_INIT_COMPLETE);
        }

    }

    httpPost<TResponse>(url: string, requestData?: object): Promise<TResponse> {
        return this.serviceFactory.http2Service().post<TResponse>(url, requestData);
    }

    httpGet<TResponse>(url: string, requestData?: object): Promise<TResponse> {
        return this.serviceFactory.http2Service().get<TResponse>(url, requestData);
    }

    waitForAutologin() {
        return this.serviceFactory.oAuth2Service().waitForAutologin();
    }

    loadScript(url: string, tagId?: string): Promise<void> {
        return this.serviceFactory.scriptLoaderService().loadScript(url, tagId);
    }

    getRefreshToken(): string {
        return this.serviceFactory.oAuth2Service().getRefreshToken();
    }

    get overlayZIndex(): SdkOverlayZIndex {
        return {
            OMG: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.OMG],
            GAME_FRAME: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.GAME_FRAME]
        };
    }

    isInAutologin(): boolean {
        return this.serviceFactory.oAuth2Service().isInAutologin();
    }

    fireDepositWidgetClosed() {
        Event.fire(SYSTEM_EVENTS.DEPOSIT_WIDGET_CLOSED);
    }


    /* Resumes the player's game after it was stopped, e.g. to display reality check */

    continuePlayingCurrentGame(): void {
        this.serviceFactory.gameService().continuePlayCurrentGame();
    }

    stopCurrentGameAutoplay(): void {
        this.serviceFactory.gameService().stopCurrentGameAutoplay();
    }

    getGeoLocationInfo():  Promise<IGeoLocationInfo | null> {
        return this.serviceFactory.countryService().getGeoLocationInfo();
    }

    async startGeoLocationOnOpenRealGame(): Promise<void> {
        await this.serviceFactory.geoLocationService().startLoginGeoProcess(true, GEO_LOCATION.REASON_OPEN_REAL_GAME);
    }

    async startAutoRetryGeoProcess(): Promise<void> {
        return await this.serviceFactory.geoLocationService().startAutoRetryGeoProcess();
    }

    isEligibleForGameGeoRetry(): boolean {
        return this.serviceFactory.geoRetryService().isEligible(true);
    }

    getRetryMessageButtons(analyticsLabel: string, resolve?, reject?): Partial<ISdkMessageButtonOptions>[] {
        return this.serviceFactory.geoRetryService().getRetryMessageButtons(analyticsLabel, resolve, reject);
    }

    getButtonsForNonRetryGeoFlow(): Partial<ISdkMessageButtonOptions>[] {
        return this.serviceFactory.geoLocationService().getButtonsForNonRetryGeoFlow('getRetryMessageButtons');
    }

    getDownloadAppLink() {
        return this.serviceFactory.skinService().getAppDownloadPageURL();
    }

    isDepositWidgetSupportedByHybrid(): boolean {
        return this.serviceFactory.hybridService().isDepositWidgetSupported();
    }

    promptPushNotificationPermissionRequest(): void {
        if(Device.hybrid.inCasino){
              this.serviceFactory.hybridService().promptPermission();
        }else {
            return this.serviceFactory.webPushService().promptPermission();
        }
    }

    openPopUpOnSecondWebView(content, timeout): void {
        this.serviceFactory.hybridService().openPopUpOnSecondWebView(content, timeout);
    }

    closePopUpOnSecondWebView(): void {
        this.serviceFactory.hybridService().closePopUpOnSecondWebView();
    }

    getBurnoutAnimationZIndex() {
        return PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.SECONDARY_MESSAGE];
    }

    get hybrid() {
        return this.serviceFactory.hybridService();
    }
    get currentRegulation() {
        return this.serviceFactory.regulationService().getRegulation();
    }

    getCurrentBrowserTabId(): string {
        return this.serviceFactory.windowService().getCurrentBrowserTabId();
    }

    get hasOpenOverlays(): boolean {
        return this.serviceFactory.overlayManagerService().isActiveOverlayExists();
    }

    get gnwp() {
        return this.serviceFactory.gnwpService();
    }

    get scriptLoader(): IScriptLoaderService {
        return this.serviceFactory.scriptLoaderService();
    }

    stopGameTimeReminders(): void {
        OptionsComponentController.create().load('ReminderSettings').off();
    }

    extractTokenProperty(tokenProperty: string) {
        return this.serviceFactory.oAuth2Service().extractTokenProperty(tokenProperty);
    }

    isLoginPopupEnabled(): boolean {
        return this.serviceFactory.regulationService().isLoginPopupEnabled();
    }

    getAccountData(): any {
        return this.serviceFactory.userService().getAccountData()
    }
}
